// AUTOPAR1 (docs/build-briefs/AUTOPAR1-automatic-par-levels.md; PRD P0.12; DEC-412): the pure half of the automatic par
// screens. No DOM, no fetch. Every figure the screens show is composed here so a test can read it, and every empty value is
// a dash.

export const WEEKDAY_VALUES = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;
export type WeekdayValue = (typeof WEEKDAY_VALUES)[number];

export const WEEKDAY_SHORT: Record<WeekdayValue, string> = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

export function weekdayName(value: string | null): string | null {
  return value === null ? null : `${value.slice(0, 1).toUpperCase()}${value.slice(1)}`;
}

/** The three steppers' own bounds, mirrored from the server (src/domain/autoParSettings.ts AUTO_PAR_BOUNDS). The server
 * refuses anything outside them; these only keep the stepper buttons from offering a value it would refuse. */
export const BUFFER_STEP_BASIS_POINTS = 500;
export const BUFFER_RANGE_BASIS_POINTS = { min: 0, max: 10_000 } as const;
export const LOOKBACK_RANGE_WEEKS = { min: 2, max: 26 } as const;
export const HISTORY_RANGE_WEEKS = { min: 1, max: 8 } as const;
export const LEAD_RANGE_DAYS = { min: 1, max: 30 } as const;
export const PACE_RANGE_DAYS = { min: 1, max: 365 } as const;

export type PaceUnit = "days" | "weeks";

/** What the order pace prompt holds while it is open. */
export interface PaceDraft {
  unit: PaceUnit;
  /** In `unit`. */
  amount: number;
  weekday: WeekdayValue | null;
  leadDays: number;
}

export function paceDays(draft: Pick<PaceDraft, "unit" | "amount">): number {
  return draft.unit === "weeks" ? draft.amount * 7 : draft.amount;
}

/** The days a vendor's stored pace reads as in the prompt: whole weeks open as weeks, anything else as days. */
export function draftFromVendor(vendor: {
  paceDays: number | null;
  orderWeekday: string | null;
  leadDays: number;
}): PaceDraft {
  const days = vendor.paceDays;
  const weekday = (WEEKDAY_VALUES as readonly string[]).includes(vendor.orderWeekday ?? "")
    ? (vendor.orderWeekday as WeekdayValue)
    : null;
  if (days === null) {
    return { unit: "weeks", amount: 2, weekday, leadDays: vendor.leadDays };
  }
  return days % 7 === 0
    ? { unit: "weeks", amount: days / 7, weekday, leadDays: vendor.leadDays }
    : { unit: "days", amount: days, weekday, leadDays: vendor.leadDays };
}

/** The day count a unit change keeps: weeks to days multiplies, days to weeks rounds to the nearest whole week. */
export function withUnit(draft: PaceDraft, unit: PaceUnit): PaceDraft {
  if (unit === draft.unit) return draft;
  const days = paceDays(draft);
  const amount = unit === "weeks" ? Math.max(1, Math.round(days / 7)) : days;
  return { ...draft, unit, amount: clampAmount(unit, amount) };
}

export function clampAmount(unit: PaceUnit, amount: number): number {
  const max = unit === "weeks" ? Math.floor(PACE_RANGE_DAYS.max / 7) : PACE_RANGE_DAYS.max;
  return Math.min(max, Math.max(1, amount));
}

export function stepAmount(draft: PaceDraft, direction: 1 | -1): PaceDraft {
  return { ...draft, amount: clampAmount(draft.unit, draft.amount + direction) };
}

export function stepLead(draft: PaceDraft, direction: 1 | -1): PaceDraft {
  return {
    ...draft,
    leadDays: Math.min(
      LEAD_RANGE_DAYS.max,
      Math.max(LEAD_RANGE_DAYS.min, draft.leadDays + direction),
    ),
  };
}

/** The preset chips: 1 to 4 weeks. Anything else is "Other". */
export const PACE_PRESET_WEEKS = [1, 2, 3, 4] as const;

export function presetOf(draft: PaceDraft): (typeof PACE_PRESET_WEEKS)[number] | "other" {
  if (draft.unit === "weeks") {
    const found = PACE_PRESET_WEEKS.find((weeks) => weeks === draft.amount);
    if (found !== undefined) return found;
  }
  return "other";
}

export function presetLabel(weeks: number): string {
  return weeks === 1 ? "1 week" : `${weeks} weeks`;
}

/** "15 days" of use covered by each par: the days between orders plus the lead days. */
export function coverageDays(draft: PaceDraft): number {
  return paceDays(draft) + draft.leadDays;
}

function plural(count: number, one: string, many: string): string {
  return count === 1 ? one : many;
}

/** "Every 2 weeks", "Every week", "Every 10 days", "Every day", or a dash for no pace. */
export function everyLabel(days: number | null): string {
  if (days === null) return "-";
  if (days % 7 === 0) {
    const weeks = days / 7;
    return weeks === 1 ? "Every week" : `Every ${weeks} weeks`;
  }
  return days === 1 ? "Every day" : `Every ${days} days`;
}

export function deliveryLabel(leadDays: number): string {
  return `delivery ${leadDays} ${plural(leadDays, "day", "days")}`;
}

/** The line under a vendor on the Settings screen: "Every 2 weeks · Monday · delivery 1 day", or a dash with no pace. */
export function paceLine(vendor: {
  paceDays: number | null;
  orderWeekday: string | null;
  leadDays: number;
}): string {
  if (vendor.paceDays === null) return "-";
  const parts = [everyLabel(vendor.paceDays)];
  const weekday = weekdayName(vendor.orderWeekday);
  if (weekday !== null) parts.push(weekday);
  parts.push(deliveryLabel(vendor.leadDays));
  return parts.join(" · ");
}

/** "Updated today", "Updated last night" or "Updated N days ago", from the run's instant, or "Not updated yet" when it has never run. */
export function updatedLabel(lastRunAt: string | null, now: Date): string {
  if (lastRunAt === null) return "Not updated yet";
  const then = new Date(lastRunAt);
  if (Number.isNaN(then.getTime())) return "Not updated yet";
  const startOfDay = (date: Date): number =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const days = Math.round((startOfDay(now) - startOfDay(then)) / 86_400_000);
  if (days <= 0) return "Updated today";
  if (days === 1) return "Updated last night";
  return `Updated ${days} days ago`;
}

export function percentLabel(basisPoints: number): string {
  return `${basisPoints / 100}`;
}

/** A count unit's plural for the used per week figure: "bottle" to "bottles/week". */
export function perWeekUnit(countUnit: string | null): string {
  return countUnit === null ? "per week" : `${countUnit}s/week`;
}

export type ChangeTag =
  | { kind: "up" | "down"; text: string }
  | { kind: "same" | "held" | "na"; text: string }
  | { kind: "would_be"; text: string };

/** The tag beside an item's auto par. A change reads "+1" or "-1" in whole count units, never a decimal sentence. */
export function changeTag(item: {
  tag: "up" | "down" | "same" | "held" | "would_be" | "na";
  change: string | null;
}): ChangeTag {
  switch (item.tag) {
    case "up":
      return { kind: "up", text: `+${trimChange(item.change)}` };
    case "down":
      return { kind: "down", text: `-${trimChange(item.change).replace(/^-/, "")}` };
    case "same":
      return { kind: "same", text: "Same" };
    case "held":
      return { kind: "held", text: "Held" };
    case "would_be":
      return { kind: "would_be", text: "Would be" };
    default:
      return { kind: "na", text: "-" };
  }
}

function trimChange(change: string | null): string {
  if (change === null) return "-";
  return change.replace(/^[-+]/, "");
}

/** A dash for a missing figure. */
export function figure(value: string | null): string {
  return value === null ? "-" : value;
}

/** The "6 days" under a missing used rate: how much history the item has. */
export function historyLabel(days: number | null): string {
  if (days === null || days <= 0) return "";
  return `${days} ${plural(days, "day", "days")}`;
}

/** INVPOLISH1: the sentence under Recalculate. A second run on the same day finds the same figures and changes no par, which
 * used to read "0 pars updated." and look like nothing happened; it now says the pars are already current. */
export function recalculationResultText(summary: { ran: boolean; applied: number; held: number }): string {
  const held = summary.held > 0 ? ` ${summary.held} held for review.` : "";
  if (!summary.ran) return `No recalculation ran. ${summary.applied} pars updated.${held}`;
  if (summary.applied === 0 && summary.held === 0) return "Already up to date for today.";
  return `${summary.applied} ${summary.applied === 1 ? "par updated" : "pars updated"}.${held}`;
}
